import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Regular tetrahedron (four equilateral faces, six equal edges) for the dashboard.
 * CSS 3D only — no three.js / react-three-fiber (matches truss-dashboard dependencies).
 */

const SQRT2 = Math.sqrt(2);
const SQRT3 = Math.sqrt(3);

type Vec3 = { x: number; y: number; z: number };

function sub(a: Vec3, b: Vec3): Vec3 {
    return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function add(a: Vec3, b: Vec3): Vec3 {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function scale(v: Vec3, s: number): Vec3 {
    return { x: v.x * s, y: v.y * s, z: v.z * s };
}

function dot(a: Vec3, b: Vec3): number {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}

function cross(a: Vec3, b: Vec3): Vec3 {
    return {
        x: a.y * b.z - a.z * b.y,
        y: a.z * b.x - a.x * b.z,
        z: a.x * b.y - a.y * b.x,
    };
}

function len(v: Vec3): number {
    return Math.sqrt(dot(v, v));
}

function normalize(v: Vec3): Vec3 {
    const L = len(v);
    if (L < 1e-12) return { x: 1, y: 0, z: 0 };
    return scale(v, 1 / L);
}

/** CSS 3D: Y positive downward. */
function mathToCss(v: Vec3): Vec3 {
    return { x: v.x, y: -v.y, z: v.z };
}

function mat4FromBasisAndTranslation(U: Vec3, V: Vec3, N: Vec3, T: Vec3): string {
    return `matrix3d(${[
        U.x, U.y, U.z, 0, V.x, V.y, V.z, 0, N.x, N.y, N.z, 0, T.x, T.y, T.z, 1,
    ].join(',')})`;
}

// --- Quaternion + 4×4 (column-major) for vertex-cycling idle pose ---

type Quat = { w: number; x: number; y: number; z: number };

function quatNorm(q: Quat): number {
    return Math.hypot(q.w, q.x, q.y, q.z);
}

function quatNormalize(q: Quat): Quat {
    const L = quatNorm(q);
    if (L < 1e-12) return { w: 1, x: 0, y: 0, z: 0 };
    return { w: q.w / L, x: q.x / L, y: q.y / L, z: q.z / L };
}

function quatFromAxisAngle(axis: Vec3, angleRad: number): Quat {
    const n = normalize(axis);
    const h = angleRad * 0.5;
    const s = Math.sin(h);
    return quatNormalize({ w: Math.cos(h), x: n.x * s, y: n.y * s, z: n.z * s });
}

/** Shortest rotation taking unit `from` to unit `to`. */
function quatAlignVectors(from: Vec3, to: Vec3): Quat {
    const f = normalize(from);
    const t = normalize(to);
    const cr = dot(f, t);
    const ax = cross(f, t);
    const s = len(ax);
    if (s < 1e-8) {
        if (cr > 0) return { w: 1, x: 0, y: 0, z: 0 };
        const ortho = Math.abs(f.x) < 0.9 ? ({ x: 1, y: 0, z: 0 } as Vec3) : ({ x: 0, y: 1, z: 0 } as Vec3);
        return quatFromAxisAngle(normalize(cross(f, ortho)), Math.PI);
    }
    return quatFromAxisAngle(normalize(ax), Math.atan2(s, cr));
}

function quatSlerp(a: Quat, b: Quat, t: number): Quat {
    let dotv = a.w * b.w + a.x * b.x + a.y * b.y + a.z * b.z;
    let b2 = b;
    if (dotv < 0) {
        dotv = -dotv;
        b2 = { w: -b.w, x: -b.x, y: -b.y, z: -b.z };
    }
    if (dotv > 0.9995) {
        return quatNormalize({
            w: a.w + t * (b2.w - a.w),
            x: a.x + t * (b2.x - a.x),
            y: a.y + t * (b2.y - a.y),
            z: a.z + t * (b2.z - a.z),
        });
    }
    const th0 = Math.acos(dotv);
    const th = th0 * t;
    const sin0 = Math.sin(th0);
    const s0 = Math.sin(th0 - th) / sin0;
    const s1 = Math.sin(th) / sin0;
    return quatNormalize({
        w: a.w * s0 + b2.w * s1,
        x: a.x * s0 + b2.x * s1,
        y: a.y * s0 + b2.y * s1,
        z: a.z * s0 + b2.z * s1,
    });
}

/** Hamilton product: R(a·b) = R(a)·R(b) on column vectors. */
function quatHamiltonProduct(a: Quat, b: Quat): Quat {
    return quatNormalize({
        w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
        x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
        y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
        z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    });
}

/** Column-major 4×4 rotation from unit quaternion. */
function mat4FromQuatR(q: Quat): number[] {
    const { w, x, y, z } = quatNormalize(q);
    const xx = x * x,
        yy = y * y,
        zz = z * z;
    const xy = x * y,
        xz = x * z,
        yz = y * z;
    const wx = w * x,
        wy = w * y,
        wz = w * z;
    const m00 = 1 - 2 * (yy + zz);
    const m01 = 2 * (xy - wz);
    const m02 = 2 * (xz + wy);
    const m10 = 2 * (xy + wz);
    const m11 = 1 - 2 * (xx + zz);
    const m12 = 2 * (yz - wx);
    const m20 = 2 * (xz - wy);
    const m21 = 2 * (yz + wx);
    const m22 = 1 - 2 * (xx + yy);
    return [m00, m10, m20, 0, m01, m11, m21, 0, m02, m12, m22, 0, 0, 0, 0, 1];
}

/** Apply 3×3 rotation block (column-major 4×4) to a vector. */
function mat3RotFromMat4(m16: number[], v: Vec3): Vec3 {
    return {
        x: m16[0] * v.x + m16[4] * v.y + m16[8] * v.z,
        y: m16[1] * v.x + m16[5] * v.y + m16[9] * v.z,
        z: m16[2] * v.x + m16[6] * v.y + m16[10] * v.z,
    };
}

function tetrahedronVertexDirections(): Vec3[] {
    return [
        mathToCss({ x: 1, y: 1, z: 1 }),
        mathToCss({ x: 1, y: -1, z: -1 }),
        mathToCss({ x: -1, y: 1, z: -1 }),
        mathToCss({ x: -1, y: -1, z: 1 }),
    ].map(normalize);
}

/**
 * Direction (CSS space, unit) that each forward apex aligns to during the idle vertex cycle.
 * Default was straight +Z; tilting toward −Y models a viewer **above** the screen plane looking
 * slightly down (elevation angle from the XY plane toward +Z).
 */
const IDLE_VIEWER_ELEVATION_DEG = 8;

function idleViewerTowardCss(): Vec3 {
    const rad = (IDLE_VIEWER_ELEVATION_DEG * Math.PI) / 180;
    return normalize({ x: 0, y: -Math.sin(rad), z: Math.cos(rad) });
}

/** Same vector for align + roll (bit-identical; roll must preserve this axis once elevation ≠ 0). */
const IDLE_VIEW_AXIS_CSS = idleViewerTowardCss();

function vertexAlignQuats(): Quat[] {
    return tetrahedronVertexDirections().map((d) => quatAlignVectors(d, IDLE_VIEW_AXIS_CSS));
}

const VERTEX_ALIGN_QUATS: Quat[] = vertexAlignQuats();

/** Vertices at ±s cube corners (same frame as `buildFaces`); scale cancels in edge directions. */
function tetrahedronVertPositionsUnit(): Vec3[] {
    const s = 1;
    return [
        mathToCss({ x: s, y: s, z: s }),
        mathToCss({ x: s, y: -s, z: -s }),
        mathToCss({ x: -s, y: s, z: -s }),
        mathToCss({ x: -s, y: -s, z: s }),
    ];
}

const TET_VERT_POS_UNIT = tetrahedronVertPositionsUnit();

/**
 * For each forward apex (aligned to `IDLE_VIEW_AXIS_CSS`), roll around that **same** viewer axis so a
 * candidate base edge is horizontal in XY. Elevation≠0 ⇒ global `rotateZ` no longer preserves the view
 * axis — it would jog the apex off `IDLE_VIEW_AXIS_CSS` between holds.
 *
 * For unit edge ê after alignment only: want ey·( R(axis, φ) ê ) = 0 with ey = (0,1,0). With ê ⟂ axis,
 * Rodrigues gives ê cos φ + (axis×ê) sin φ ⇒ A cos φ + B sin φ = 0 ⇒ φ = atan2(A, −B) (+ π for the twin).
 */
function idlePoseQuatForForwardApex(apexIndex: number, verts: Vec3[]): Quat {
    const nView = IDLE_VIEW_AXIS_CSS;
    const qAlign = VERTEX_ALIGN_QUATS[apexIndex];
    const Malign = mat4FromQuatR(qAlign);
    const baseIdx = [0, 1, 2, 3].filter((i) => i !== apexIndex);
    const a = baseIdx[0]!,
        b = baseIdx[1]!,
        c = baseIdx[2]!;
    const edges: [number, number][] = [
        [a, b],
        [b, c],
        [c, a],
    ];

    const ey = { x: 0, y: 1, z: 0 };

    let bestRoll = 0;
    let bestMidY = -Infinity;
    let bestLoMax = -Infinity;
    let bestLoMin = -Infinity;

    for (const [i, j] of edges) {
        const d = normalize(sub(verts[j], verts[i]));
        const ei = mat3RotFromMat4(Malign, d);
        const A = dot(ey, ei);
        const B = dot(ey, cross(nView, ei));
        if (Math.hypot(A, B) < 1e-12) continue;
        const phi0 = Math.atan2(A, -B);
        for (const roll of [phi0, phi0 + Math.PI]) {
            const qTw = quatFromAxisAngle(nView, roll);
            const qFull = quatHamiltonProduct(qTw, qAlign);
            const Mfull = mat4FromQuatR(qFull);
            const pi = mat3RotFromMat4(Mfull, verts[i]);
            const pj = mat3RotFromMat4(Mfull, verts[j]);
            const midY = (pi.y + pj.y) * 0.5;
            const loMax = Math.max(pi.y, pj.y);
            const loMin = Math.min(pi.y, pj.y);
            if (
                midY > bestMidY + 1e-9 ||
                (Math.abs(midY - bestMidY) < 1e-9 &&
                    (loMax > bestLoMax + 1e-9 ||
                        (Math.abs(loMax - bestLoMax) < 1e-9 && loMin > bestLoMin + 1e-9)))
            ) {
                bestRoll = roll;
                bestMidY = midY;
                bestLoMax = loMax;
                bestLoMin = loMin;
            }
        }
    }

    const rollNorm = Math.atan2(Math.sin(bestRoll), Math.cos(bestRoll));
    return quatHamiltonProduct(quatFromAxisAngle(nView, rollNorm), qAlign);
}

/** Per forward-apex idle pose: apex along view axis; roll about that axis so the horizontal base edge sits low. */
const IDLE_POSE_QUATS: Quat[] = [0, 1, 2, 3].map((apex) => idlePoseQuatForForwardApex(apex, TET_VERT_POS_UNIT));

/**
 * Idle: cycle each vertex toward the viewer (see `IDLE_VIEWER_ELEVATION_DEG`), with the **bottom** base edge
 * horizontal at each apex. `VERTEX_CYCLE_MS` is one full lap; each segment ease-in/ease-out slerps to the next
 * pose — continuous motion with smooth angular velocity joins at vertices.
 */
const VERTEX_CYCLE_MS = 10000;

/** Perlin smootherstep — 1st/2nd derivatives 0 at 0 and 1 for gentle apex handoffs before slerp. */
function smootherstep01(t: number): number {
    const x = Math.max(0, Math.min(1, t));
    return x * x * x * (x * (x * 6 - 15) + 10);
}

function vertexQuatAtTimeline(tMs: number): Quat {
    const quats = IDLE_POSE_QUATS;
    const n = quats.length;
    const segLen = VERTEX_CYCLE_MS / n;
    const cyc = ((tMs % VERTEX_CYCLE_MS) + VERTEX_CYCLE_MS) % VERTEX_CYCLE_MS;
    const seg = Math.min(n - 1, Math.floor(cyc / segLen));
    const u = segLen > 1e-9 ? (cyc - seg * segLen) / segLen : 0;
    const prev = quats[(seg + n - 1) % n];
    const cur = quats[seg];
    return quatSlerp(prev, cur, smootherstep01(u));
}

function mulMatVec(U: Vec3, V: Vec3, N: Vec3, T: Vec3, p: Vec3): Vec3 {
    return {
        x: U.x * p.x + V.x * p.y + N.x * p.z + T.x,
        y: U.y * p.x + V.y * p.y + N.y * p.z + T.y,
        z: U.z * p.x + V.z * p.y + N.z * p.z + T.z,
    };
}

function projOntoPlane(u: Vec3, n: Vec3): Vec3 {
    return sub(u, scale(n, dot(u, n)));
}

function dist(a: Vec3, b: Vec3): number {
    return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function bestFaceRigid(
    p0: Vec3,
    p1: Vec3,
    p2: Vec3,
    lc: Vec3,
    localCorners: Vec3[]
): { U: Vec3; V: Vec3; N: Vec3; T: Vec3 } {
    const cw = scale(add(add(p0, p1), p2), 1 / 3);
    let n = normalize(cross(sub(p1, p0), sub(p2, p0)));
    if (dot(n, cw) < 0) n = scale(n, -1);

    const edgeCandidates = [sub(p1, p0), sub(p2, p0), sub(p2, p1)];
    const worldVerts = [p0, p1, p2];

    let best: { U: Vec3; V: Vec3; N: Vec3; T: Vec3 } | null = null;
    let bestErr = Number.POSITIVE_INFINITY;

    for (const e of edgeCandidates) {
        let u = projOntoPlane(e, n);
        u = normalize(u);
        if (len(u) < 1e-9) continue;
        const v = normalize(cross(n, u));
        const rLc = mulMatVec(u, v, n, { x: 0, y: 0, z: 0 }, lc);
        const T = { x: cw.x - rLc.x, y: cw.y - rLc.y, z: cw.z - rLc.z };

        let maxErr = 0;
        for (const corner of localCorners) {
            const w = mulMatVec(u, v, n, T, corner);
            let dMin = Number.POSITIVE_INFINITY;
            for (const pv of worldVerts) {
                dMin = Math.min(dMin, dist(w, pv));
            }
            maxErr = Math.max(maxErr, dMin);
        }

        if (maxErr < bestErr) {
            bestErr = maxErr;
            best = { U: u, V: v, N: n, T };
        }
    }

    if (!best) {
        throw new Error('SpinningTetrahedron: could not place face');
    }
    return best;
}

function buildFaces(
    edgePx: number,
    localCorners: Vec3[],
    lc: Vec3,
    faceColors: string[]
): { transform: string; background: string }[] {
    const s = edgePx / (2 * SQRT2);
    const raw: Vec3[] = [
        { x: s, y: s, z: s },
        { x: s, y: -s, z: -s },
        { x: -s, y: s, z: -s },
        { x: -s, y: -s, z: s },
    ].map(mathToCss);

    const facesIdx: [number, number, number][] = [
        [1, 2, 3],
        [0, 2, 3],
        [0, 1, 3],
        [0, 1, 2],
    ];

    return facesIdx.map((idx, fi) => {
        const p0 = raw[idx[0]];
        const p1 = raw[idx[1]];
        const p2 = raw[idx[2]];
        const { U, V, N, T } = bestFaceRigid(p0, p1, p2, lc, localCorners);
        return {
            transform: mat4FromBasisAndTranslation(U, V, N, T),
            background: faceColors[fi],
        };
    });
}

// ---------------------------------------------------------------------------
// Types (same public shape as the former R3F version)
// ---------------------------------------------------------------------------

/**
 * Mesh edge length = pane min side ÷ STAGE_EDGE_RATIO (not CSS margin—the mesh is deliberately
 * smaller than the stage for rotation/perspective clearance).
 */
const STAGE_EDGE_RATIO = 1.46;

/**
 * In-plane scale on each face so shared edges overlap (subpixel / anti-alias seams).
 * Applied on an inner layer with origin at the triangle centroid — scaling from `0 0` skews
 * the clip and leaves real gaps along some edges.
 * High-DPR / narrow viewports often need a touch more overlap than desktop.
 */
const FACE_PLANE_OVERLAP_SCALE_DESKTOP = 1.0022;
const FACE_PLANE_OVERLAP_SCALE_MOBILE = 1.003;

/** Match narrow layouts (typical phones; tablets in portrait often match too). */
const FACE_OVERLAP_MOBILE_MQ = '(max-width: 768px)';

export interface TetrahedronProps {
    /** Width of the outer container (e.g. `'100%'` or `320`). */
    width?: string | number;
    /** Viewport height: number (px) or `'100%'` to fill a sized parent. */
    height?: number | string;
    /** Extra classes on the root (e.g. `h-full min-h-0 flex-1` to fill a flex parent). */
    className?: string;
    /** Show edge-length proof line below the view. */
    showProof?: boolean;
    /** Show a short caption under the view (e.g. that the logo animates automatically). */
    showCaption?: boolean;
    /** Vertical alignment of the 3D mesh inside the view pane (default centers). */
    justifyMesh?: 'start' | 'center';
    /**
     * Vertical placement of the mesh pivot within the stage (row flex cross-axis).
     * Use `bottom` to sit the shape near the lower edge of the pane (e.g. above a wordmark).
     */
    meshStageAlign?: 'center' | 'bottom';
    /**
     * If set, mesh edge/stage are computed as if the pane’s min side were this many px (e.g. `400`
     * while the real layout box is `350` so the tet stays the same size; use `overflow-visible` on
     * ancestors if the stage is larger than the slot).
     */
    meshBasisMinPx?: number;
    /** Fired when mesh layout changes (e.g. resize); `edgePx` matches rendered tetrahedron edge length. */
    onLayoutMetrics?: (metrics: { edgePx: number }) => void;
}

/** Vertex triplets + colors aligned with former three.js face order (by vertex set). Blues ~5% higher saturation vs prior hex. */
const FACE_COLORS_BY_MESH_FACE = ['#b0bec5', '#8fafc8', '#2b5a90', '#477ab8'];

// ---------------------------------------------------------------------------
// Mesh
// ---------------------------------------------------------------------------

function TetrahedronMesh({
    edgePx,
    stagePx,
    meshStageAlign = 'center',
}: {
    edgePx: number;
    stagePx: number;
    meshStageAlign?: 'center' | 'bottom';
}) {
    const H = (edgePx * SQRT3) / 2;

    const faces = useMemo(() => {
        const lc = { x: edgePx / 2, y: (2 * H) / 3, z: 0 };
        const localCorners: Vec3[] = [
            { x: edgePx / 2, y: 0, z: 0 },
            { x: 0, y: H, z: 0 },
            { x: edgePx, y: H, z: 0 },
        ];
        return buildFaces(edgePx, localCorners, lc, FACE_COLORS_BY_MESH_FACE);
    }, [edgePx]);

    const rollRef = useRef<HTMLDivElement>(null);
    const spinRef = useRef<HTMLDivElement>(null);
    const reducedMotionRef = useRef(false);
    const spinStartRef = useRef(performance.now());

    const [faceOverlapScale, setFaceOverlapScale] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia(FACE_OVERLAP_MOBILE_MQ).matches
            ? FACE_PLANE_OVERLAP_SCALE_MOBILE
            : FACE_PLANE_OVERLAP_SCALE_DESKTOP
    );

    useLayoutEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => {
            reducedMotionRef.current = mq.matches;
        };
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    useLayoutEffect(() => {
        const mq = window.matchMedia(FACE_OVERLAP_MOBILE_MQ);
        const sync = () => {
            setFaceOverlapScale(mq.matches ? FACE_PLANE_OVERLAP_SCALE_MOBILE : FACE_PLANE_OVERLAP_SCALE_DESKTOP);
        };
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        spinStartRef.current = performance.now();
        let raf = 0;

        const tick = () => {
            const now = performance.now();
            const rollEl = rollRef.current;
            const spinEl = spinRef.current;
            if (rollEl && spinEl) {
                rollEl.style.transform = 'rotateZ(0deg)';
                if (reducedMotionRef.current) {
                    spinEl.style.transform = `matrix3d(${mat4FromQuatR(IDLE_POSE_QUATS[0]).join(',')})`;
                } else {
                    const t = now - spinStartRef.current;
                    const qV = vertexQuatAtTimeline(t);
                    spinEl.style.transform = `matrix3d(${mat4FromQuatR(qV).join(',')})`;
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [edgePx, stagePx]);

    return (
        <div
            className="relative mx-auto flex shrink-0 items-center justify-center overflow-visible [transform-style:preserve-3d]"
            style={{
                width: stagePx,
                height: stagePx,
                maxWidth: '100%',
                maxHeight: '100%',
                perspective: Math.max(560, edgePx * 3.85),
                perspectiveOrigin: '50% 50%',
            }}
        >
            {/* Row flex: cross-axis aligns mesh vertically in stage (`bottom` = toward caption / wordmark). */}
            <div
                className={`pointer-events-none absolute inset-0 flex justify-center [transform-style:preserve-3d] ${meshStageAlign === 'bottom' ? 'items-end' : 'items-center'}`.trim()}
            >
                <div
                    ref={rollRef}
                    className="spinning-tetrahedron__roll relative h-0 w-0 [transform-style:preserve-3d]"
                    style={{ transformOrigin: '0px 0px 0px' }}
                >
                    <div
                        ref={spinRef}
                        className="spinning-tetrahedron__spin relative h-0 w-0 [transform-style:preserve-3d]"
                        style={{ transformOrigin: '0px 0px 0px' }}
                    >
                        {faces.map((face, i) => (
                            <div
                                key={i}
                                className="absolute left-0 top-0 [backface-visibility:hidden] [transform-style:preserve-3d]"
                                style={{
                                    width: edgePx,
                                    height: H,
                                    transform: face.transform,
                                    transformOrigin: '0 0',
                                }}
                            >
                                <div
                                    className="[backface-visibility:hidden]"
                                    style={{
                                        width: edgePx,
                                        height: H,
                                        transform: `scale3d(${faceOverlapScale}, ${faceOverlapScale}, 1)`,
                                        transformOrigin: '50% 66.6666667%',
                                        clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                                        background: face.background,
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function Tetrahedron({
    width = '100%',
    height = 460,
    className = '',
    showProof = true,
    showCaption = true,
    justifyMesh = 'center',
    meshStageAlign = 'center',
    meshBasisMinPx,
    onLayoutMetrics,
}: TetrahedronProps) {
    const paneRef = useRef<HTMLDivElement>(null);
    const [box, setBox] = useState({ w: 0, h: 0 });
    const onLayoutMetricsRef = useRef(onLayoutMetrics);
    onLayoutMetricsRef.current = onLayoutMetrics;

    useLayoutEffect(() => {
        const el = paneRef.current;
        if (!el) return;
        const read = () => {
            const r = el.getBoundingClientRect();
            setBox({ w: r.width, h: r.height });
        };
        read();
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const numericHeight = typeof height === 'number' ? height : undefined;

    const { edgePx, stagePx } = useMemo(() => {
        const w = box.w > 1 ? box.w : typeof width === 'number' ? width : 0;
        const h = box.h > 1 ? box.h : numericHeight ?? 0;
        const sMeasured = w > 0 && h > 0 ? Math.min(w, h) : 0;
        const sBasis =
            meshBasisMinPx != null && meshBasisMinPx > 1
                ? Math.floor(meshBasisMinPx)
                : sMeasured > 0
                  ? Math.floor(sMeasured)
                  : 0;
        if (sBasis > 0) {
            const stage = sBasis;
            const edge = Math.max(48, Math.floor(stage / STAGE_EDGE_RATIO));
            return { edgePx: edge, stagePx: stage };
        }
        const fallback = numericHeight ?? 360;
        const edge = Math.max(80, Math.round(fallback / STAGE_EDGE_RATIO));
        return { edgePx: edge, stagePx: Math.ceil(edge * 1.85) };
    }, [box.w, box.h, width, numericHeight, meshBasisMinPx]);

    useLayoutEffect(() => {
        onLayoutMetricsRef.current?.({ edgePx });
    }, [edgePx]);

    const proof = useMemo(() => {
        if (!showProof) {
            return { length: '', allEqual: true };
        }
        const s = edgePx / (2 * SQRT2);
        const verts: Vec3[] = [
            { x: s, y: s, z: s },
            { x: s, y: -s, z: -s },
            { x: -s, y: s, z: -s },
            { x: -s, y: -s, z: s },
        ].map(mathToCss);
        const edges: [number, number][] = [
            [0, 1],
            [0, 2],
            [0, 3],
            [1, 2],
            [1, 3],
            [2, 3],
        ];
        const lengths = edges.map(([a, b]) => dist(verts[a], verts[b]));
        const allEqual = lengths.every((l) => Math.abs(l - lengths[0]) < 1e-6);
        return { length: lengths[0].toFixed(4), allEqual };
    }, [edgePx, showProof]);

    const widthStyle = typeof width === 'number' ? `${width}px` : width;
    const heightStyle: React.CSSProperties =
        typeof height === 'number'
            ? { height }
            : typeof height === 'string'
              ? { height, minHeight: 0 }
              : {};

    return (
        <>
            <style>{`
        .spinning-tetrahedron__roll,
        .spinning-tetrahedron__spin {
          transform-style: preserve-3d;
          transform-origin: 0px 0px 0px;
        }
      `}</style>
            <div
                className={`flex min-h-0 min-w-0 flex-col items-center text-muted-foreground ${className}`.trim()}
                style={{ width: widthStyle, ...heightStyle }}
                role="img"
                aria-label="Truss"
            >
                <div
                    ref={paneRef}
                    className={`flex min-h-0 w-full flex-1 flex-col items-center overflow-visible pb-0 pt-0 ${justifyMesh === 'start' ? 'justify-start' : 'justify-center'}`}
                    style={{
                        width: '100%',
                        minHeight: 0,
                        height: typeof height === 'number' ? '100%' : height === '100%' ? '100%' : undefined,
                    }}
                >
                    <TetrahedronMesh edgePx={edgePx} stagePx={stagePx} meshStageAlign={meshStageAlign} />
                </div>

                {showProof && (
                    <p className="mt-2 text-center font-mono text-xs">
                        All 6 edge lengths: {proof.length}px&nbsp;|&nbsp; All equal:{' '}
                        {proof.allEqual ? '✓ yes' : '✗ no'}
                    </p>
                )}

                {showCaption && (
                    <p className="mt-1 text-center text-xs">Automatically rotating logo</p>
                )}
            </div>
        </>
    );
}

